import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import DashboardTabs from '@components/common/Dashboard/DashboardTabs';
import Filter from '@components/eco-summary/Filter';
import SummaryTable from '@components/eco-summary/SummaryTable';
import { PageContainer, AlertTime } from './styled';
import moment from 'moment';
import { Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { createTempDashboard } from '@store/tempDashboard/actions';
import { DASHBOARD_TYPE } from '@utils/constants';
import { useSummaryHook } from './hooks';
import { GetSeverityDisplay } from '@components/eco-alert-detail/ImpactDetail';

export interface SummaryItem {
  id: string;
  status: string;
  severity: string;
  metrics: string;
  cohort: string;
  impactedUsers: string;
  impactedServices: string;
  rootCause: string;
  duration: string;
  timeFired: number;
}

/**
 * Because this page is pretty easy, so we just use the react built-in state to manage the summary data
 *
 * All the filter related states and table data are in this index.tsx file
 *
 * There are 2 useEffects in this file,
 * One for monitoring the time interval change, the other one is for monitoring other filter values change
 *
 * This page consists of 2 parts.
 * #1 Filter
 * Any filter value changing will call onFilterChange function to change the react state value
 * this will trigger the table data filter logic or fetching data from server logic
 *
 * #2 Table
 * Just receive the table data and render
 */
export default function Eco2AIAlertSummary() {
  // filter related state
  const [timeInterval, setTimeInterval] = useState([moment().subtract(24, 'hours'), moment()]);
  const [searchWord, setSearchWord] = useState('');
  const [status, setStatus] = useState<string[]>([]);
  const [severity, setSeverity] = useState<string[]>([]);
  const [metricList, setMetricList] = useState<string[]>([]);
  const dispatch = useDispatch();

  const {
    fetchData,
    // original alert table data
    originalDataSource,
    // table loading
    loading,
    // all candidate metrics
    allMetricList,
  } = useSummaryHook();

  // the data displayed in the page
  const [currentFilteredDataSource, setCurrentFilteredDataSource] = useState([]);

  // All table columns
  const columns: ColumnsType<SummaryItem> = useMemo(() => {
    return [
      {
        title: 'ID',
        dataIndex: 'id',
      },
      {
        title: 'Status',
        dataIndex: 'status',
      },
      {
        title: 'Severity',
        dataIndex: 'severity',
        render(data) {
          return GetSeverityDisplay(data);
        },
      },
      {
        title: 'Experience Metrics Affected',
        dataIndex: 'metrics',
      },
      {
        title: 'Impacted Device Cohort',
        dataIndex: 'cohort',
      },
      {
        title: 'Numbers Of Devices Impacted',
        dataIndex: 'impactedUsers',
      },
      {
        title: 'Root Cause',
        dataIndex: 'rootCause',
      },
      {
        title: 'Duration',
        dataIndex: 'duration',
      },
      {
        title: 'Time Alert Fired',
        dataIndex: 'timeFired',
        defaultSortOrder: 'descend',
        sorter: (a, b) => (a.timeFired > b.timeFired ? 1 : -1),
        render(data, row) {
          return (
            <AlertTime
              onClick={() => {
                dispatch(
                  createTempDashboard(
                    DASHBOARD_TYPE.EXPERIENCE_ALERT_DIAGNOSTICS,
                    { alertId: row.id },
                    `Experience Alert - ${row.cohort}`,
                  ),
                );
              }}
            >
              {moment(data).format('hh:mm A MMM Do, YYYY')}
            </AlertTime>
          );
        },
      },
    ];
  }, [currentFilteredDataSource]);

  // selected displaying columns
  const [columnList, setColumnList] = useState(
    columns.map((column) => {
      return {
        title: column.title,
        show: true,
      };
    }),
  );

  // fetch data from server only when time interval changes
  useEffect(() => {
    fetchData(timeInterval);
  }, [timeInterval]);

  // search logic triggered by filter state change
  // after the filter value changes or getting a new response from server, this logic will run again
  useEffect(() => {
    const filterData = originalDataSource.filter((rowData: SummaryItem) => {
      const statusValid = status.length === 0 || status.includes(rowData.status);
      const severityValid = severity.length === 0 || severity.includes(rowData.severity);
      const metricValid = metricList.length === 0 || metricList.includes(rowData.metrics);

      const lowerCaseSearchWordStr = searchWord.toLowerCase();
      const searchWordValid =
        searchWord === '' ||
        `${rowData.id}`.includes(searchWord) ||
        rowData.status.toLowerCase().includes(lowerCaseSearchWordStr) ||
        rowData.severity.toLowerCase().includes(lowerCaseSearchWordStr) ||
        rowData.metrics.toLowerCase().includes(lowerCaseSearchWordStr) ||
        rowData.cohort.toLowerCase().includes(lowerCaseSearchWordStr) ||
        `${rowData.impactedUsers}`.includes(lowerCaseSearchWordStr);

      return statusValid && severityValid && metricValid && searchWordValid;
    });

    setCurrentFilteredDataSource(filterData);
  }, [searchWord, status, severity, metricList, originalDataSource]);

  // filter change update the states
  // useEffect will finish the filter logic in frontend
  const onFilterChange = (setStateFuncName, value) => {
    switch (setStateFuncName) {
      case 'setTimeInterval':
        setTimeInterval(value);
        break;
      case 'setSearchWord':
        setSearchWord(value);
        break;
      case 'setStatus':
        setStatus(value);
        break;
      case 'setSeverity':
        setSeverity(value);
        break;
      case 'setMetricList':
        setMetricList(value);
        break;
      case 'setColumnList':
        setColumnList(value);
        break;
    }
  };

  return (
    <PageContainer>
      <DashboardTabs />
      {/* Filter part */}
      <Filter
        timeInterval={timeInterval}
        columnList={columnList}
        onFilterChange={onFilterChange}
        allMetricList={allMetricList}
      />
      {/* Table part */}
      <SummaryTable
        loading={loading}
        data={currentFilteredDataSource}
        columns={columns.filter((col) => {
          return columnList.find((c) => c.title === col.title)?.show;
        })}
      />
    </PageContainer>
  );
}
